import { Link } from 'react-router-dom';
import { formatDateTimeISO, formatDateISO, formatFileSize } from '../../lib/dateUtils';
import type { FileWithSession } from '../../hooks/useFiles';

interface Props {
  file: FileWithSession;
  canDelete: boolean;
  isDeleting: boolean;
  onDownload: (file: FileWithSession) => void;
  onDelete: (file: FileWithSession) => void;
}

const FILE_ICON: Record<string, string> = {
  audio: '🎵',
  text: '📄',
  transcript: '📄',
  document: '📎',
};

export function FileListItem({ file, canDelete, isDeleting, onDownload, onDelete }: Props) {
  return (
    <div className="flex items-start justify-between p-4 border border-steel/20 rounded-md hover:bg-ice/50 transition-colors">
      <div className="flex items-start space-x-4 flex-1 min-w-0">
        <span className="text-3xl flex-shrink-0">{FILE_ICON[file.file_type] ?? '📎'}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-navy truncate mb-1">{file.file_name}</p>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-steel mb-2">
            <span>{formatFileSize(file.file_size)}</span>
            <span>•</span>
            <span className="capitalize">{file.file_type}</span>
            <span>•</span>
            <span>{formatDateTimeISO(file.created_at)}</span>
            <span>•</span>
            <span>Uploaded by {file.uploaded_by_name}</span>
          </div>

          {file.session_id && (
            <div className="flex items-center space-x-2 text-xs">
              <span className="px-2 py-0.5 bg-orca-elevate/10 text-orca-elevate rounded-md font-medium">
                📝 Session: {formatDateISO(file.session_date)}
              </span>
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md">
                👤 {file.trainee_name}
              </span>
              <span className="px-2 py-0.5 bg-steel/10 text-steel rounded-md">
                ✈️ {file.aircraft_type}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end space-y-2 ml-4 flex-shrink-0">
        {file.session_id && (
          <Link
            to={`/sessions/${file.session_id}`}
            className="px-3 py-1 text-xs font-medium text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
          >
            View Session
          </Link>
        )}
        <button
          onClick={() => onDownload(file)}
          className="px-3 py-1 text-xs font-medium text-orca-elevate hover:text-orca-elevate/80 transition-colors"
        >
          Download
        </button>
        {canDelete && (
          <button
            onClick={() => onDelete(file)}
            disabled={isDeleting}
            className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}
